import DataGrid, {
    Column, DataGridRef, DataGridTypes, FilterRow, IColumnProps, Paging, Scrolling, Selection
} from 'devextreme-react/data-grid';
import { useGetTablesStructureQuery } from "../../api/rtk/base/slices/tablesApiSlice";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { getLongestString, getValueWithMapLinks, prepareTableData, transformArrayWithEnums } from "../../features/dataFormatFunctions/dataFormatter";
import { store } from "../../stateManagement/store";
import { getItemWebStorage } from "../../features/webStorageSaver";
import { getBooleanBodyDevExtreme, getMapLinkHeaderCell, getObjectsTableBasicBodyDevExtreme } from "../../widgets/tablesShared/tableTools/tableCellBodies";
import { useDispatch } from "react-redux";
import CustomStore from "devextreme/data/custom_store";
import { getFetchTableUrl, prepareQueryArgsForTableFetchDevExtreme } from "../../widgets/tablesShared/tableTools/fetchTableValue";
import { tableBooleanEnums } from "../../widgets/table/tableFeatures/tableEnums";
import DataSource from "devextreme/data/data_source";
import { useAppSelector } from "../../stateManagement/hooks/redux";
import { Loader } from "../../widgets/loaders/Loader";
import { setTableInfoWindowIsShown } from "../../stateManagement/reducers/ContentHeaderSlice";
import ContentHeader from '../../widgets/contentHeader/ContentHeader';

const close_icon = require('../../assets/images/icons/close_icon.png')
const direction_icon = require('../../assets/images/icons/direction_icon.png')
const filter_icon = require('../../assets/images/icons/filter_icon.png')
const found_results_icon = require('../../assets/images/icons/found_results_icon.png')
const objects_icon = require('../../assets/images/icons/objects_icon.png')
const road_icon = require('../../assets/images/icons/road_icon.png')

export const tableFilterOperationsObject: any = {
    "int": ["=", "<>", "<", ">", "<=", ">=", "between"],
    "float": ["=", "<>", "<", ">", "<=", ">=", "between"],
    "string": ["contains", "notcontains", "startswith", "endswith", "=", "<>"],
    "date": ["=", "<>", "<", ">", "<=", ">=", "between"],
}

export default function DevExtremeObjectsTablePage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { objectTypeName }: any = useParams()
    const [columns, setColumns] = useState<Array<IColumnProps>>([])
    const [totalCount, setTotalCount] = useState()
    const [tableName, setTableName] = useState<any>()
    const [tableValueUrl, setTableValueUrl] = useState<any>()
    const { pathId, pathName, roadName } = useAppSelector(state => state.pathsInfo.selectedPath) ?? {}
    const [valueIsLoading, setValueIsLoading] = useState(false)
    const dataGridRef = useRef<DataGridRef>(null);
    const tableInfoWindowIsShown = useAppSelector(state => state.contentHeader.tableInfoWindowIsShown)
    const objectTypes = getItemWebStorage('objectTypes')
    const currentConfig = getItemWebStorage("currentConfig");
    const structureSuffix = currentConfig?.structureSuffix

    useEffect(() => {
        const name = objectTypes.find((objectType: any) => objectType.name === objectTypeName)?.caption
        name ? setTableName(`Таблица «${name}»`) : setTableName('Таблица «...»')
    }, [objectTypeName])

    // Запрос структуры таблицы
    const {
        data: tablesStructure,
    } = useGetTablesStructureQuery({ structureSuffix })

    useEffect(() => {
        if (tablesStructure && objectTypeName) {
            let columns: Array<IColumnProps> = []
            let columnsStructure: any = tablesStructure[objectTypeName]
            setTableValueUrl(columnsStructure?.url)
            columnsStructure && Object.entries(columnsStructure.columns).map(([key, value]: any) => {
                let name = typeof value?.name === typeof [] ? getLongestString(value?.name) : value?.name
                let alignment: any = "right"
                if ('header' in columnsStructure?.columns[key]) {
                    name = value?.header
                }
                let cellRender = getObjectsTableBasicBodyDevExtreme
                if (tableBooleanEnums[key]) {
                    cellRender = getBooleanBodyDevExtreme
                    alignment = "center"
                }
                let filterOperations = tableFilterOperationsObject[value?.type]
                let column: IColumnProps = {
                    dataField: key,
                    caption: name,
                    dataType: value?.type,
                    filterOperations: filterOperations,
                    filterType: "include",
                    allowResizing: true,
                    allowFiltering: key !== 'MaintenancePosition',
                    allowSorting: true,
                    minWidth: 100,
                    alignment,
                    cellRender: cellRender
                }
                columns.push(column)
            })
            columns.push({
                dataField: "MapLink",
                dataType: "string",
                alignment: "center",
                allowFiltering: false,
                allowSorting: false,
                allowResizing: true,
                minWidth: 55,
                width: 55,
                headerCellRender: getMapLinkHeaderCell,
                // cellRender: getMapLinkBodyCell
            })
            setColumns(columns)
        }
    }, [tablesStructure, objectTypeName])

    function onSelectionChanged({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) {
        navigate(`/app/table/${objectTypeName}/${selectedRowsData[0]?.InfoObjectID}`);
    }

    function handleErrors(response: any) {
        if (!response.ok) {
            throw Error(response.statusText);
        }
        return response;
    }

    const [customDataSource, setCustomDataSource] = useState<any>(null)

    function getCustomSource() {
        return new DataSource({
            store: new CustomStore({
                load: (loadOptions: any) => {
                    const { sortQueryArgsParameters, filterStringArray } = prepareQueryArgsForTableFetchDevExtreme(loadOptions, columns)
                    let skip = loadOptions?.skip ?? 0
                    let url = getFetchTableUrl(tableValueUrl, skip, 200, sortQueryArgsParameters, filterStringArray, pathId)
                    const token = store.getState().auth.tokens.app
                    setValueIsLoading(true)
                    return fetch(url, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                    })
                        .then(handleErrors)
                        .then(response => response.json())
                        .then(response => {
                            let value = response.value
                            value = transformArrayWithEnums(value)
                            value = getValueWithMapLinks(value, objectTypeName)
                            value = prepareTableData(value, columns, true)
                            setTotalCount(response["@xdata.count"])
                            setValueIsLoading(false)
                            return {
                                data: value,
                                totalCount: response["@xdata.count"],
                            };
                        })
                        .catch(() => {
                            setValueIsLoading(false)
                            throw 'Ошибка';
                        });
                }
            })
        })
    }

    useEffect(() => {
        setCustomDataSource(getCustomSource())
    }, [tableValueUrl])

    return (
        <>
            <ContentHeader title={tableName} type="devExtremeTable" />
            {/* Отображение подробной информации о таблице */}
            <div className="objects-table-div">
                {tableInfoWindowIsShown &&
                    <div className="table-info">
                        <h5 className="table-info-head">В таблице отображены:</h5>
                        <p><img src={objects_icon} width="15px" height="15px" /><strong>Объекты:</strong> <span className="table-info-value table-info-base">{tableName}</span></p>
                        <p><img src={road_icon} width="15px" height="15px" /><strong>По дороге:</strong> <span className="table-info-value table-info-base">{roadName != null ? roadName : <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}</span></p>
                        <p><img src={direction_icon} width="15px" height="15px" /><strong>Направление:</strong> <span className="table-info-value table-info-base">{pathName != null ? pathName : <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}</span></p>
                        <p><img src={found_results_icon} width="15px" height="15px" /><strong>Найдено объектов:</strong> <span className="table-info-value table-info-base">{totalCount != null ? totalCount : <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}</span></p>
                        <p className="table-info-section"><strong>Фильтрации данных в таблице:</strong></p>
                        <p className="table-info-filter"><img src={filter_icon} width="13px" height="13px" />Фильтр к столбцу с числовыми данными:</p>
                        <ul className="table-info-filter">
                            <li>Больше, чем: {">"} X</li>
                            <li>Меньше, чем: {"<"} X</li>
                            <li>Диапазон данных: X-Y</li>
                        </ul>
                        <p className="table-info-filter"><img src={filter_icon} width="13px" height="13px" />Текстовые данные фильтруются по подстроке.</p>
                        <p><img src={filter_icon} width="13px" height="13px" />Фильтр к логическим данным ( и ): 1 или 0 соответственно.</p>
                        <div className="table-info-close-div" onClick={() => dispatch(setTableInfoWindowIsShown(false))}>
                            <img src={close_icon} width="25px" height="25px" />
                        </div>
                    </div>
                }
                {valueIsLoading && <Loader />}
                {tableValueUrl &&
                    <DataGrid
                        ref={dataGridRef}
                        dataSource={customDataSource}
                        width="100%"
                        className="objects-table"
                        showRowLines
                        onSelectionChanged={onSelectionChanged}
                        hoverStateEnabled
                        remoteOperations
                        showColumnLines
                        loadPanel={undefined}
                    >
                        <FilterRow
                            applyFilter="auto"
                            visible
                            resetOperationText="Очистить"
                        />
                        <Scrolling mode="virtual" rowRenderingMode="virtual" />
                        <Selection mode="single" />
                        {columns.map(column => (
                            <Column
                                dataField={column?.dataField}
                                caption={column?.caption}
                                dataType={column?.dataType}
                                filterOperations={column?.filterOperations}
                                filterType={column?.filterType}
                                headerCellRender={column?.headerCellRender}
                                allowResizing={column?.allowResizing}
                                allowFiltering={column?.allowFiltering}
                                allowSorting={column?.allowSorting}
                                cellRender={column?.cellRender}
                                alignment={column?.alignment}
                                minWidth={column?.minWidth}
                            />
                        ))}
                        <Paging defaultPageSize={200} />
                    </DataGrid>
                }
            </div>
        </>
    )
}